/* Scoped making-video enhancement. Never loads YouTube until privacy + visibility permit it. */
(() => {
  if (customElements.get('miniami-making-video')) return;
  let privacyReady;
  const loadPrivacy = () => {
    if (window.Shopify?.customerPrivacy) return Promise.resolve();
    if (!privacyReady) privacyReady = new Promise(resolve => {
      if (!window.Shopify?.loadFeatures) { resolve(); return; }
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], () => resolve());
    });
    return privacyReady;
  };
  customElements.define('miniami-making-video', class extends HTMLElement {
    connectedCallback() {
      if (this.dispose) return;
      const id = this.dataset.youtubeId;
      if (!/^[A-Za-z0-9_-]{11}$/.test(id || '')) return;
      const screen = this.querySelector('[data-video-screen]');
      const button = this.querySelector('[data-video-play]');
      const status = this.querySelector('[data-video-status]');
      const actions = this.querySelector('.miniami-making-video__actions');
      if (!screen || !button || !status || !actions) return;
      const abort = new AbortController();
      const on = (target, type, fn) => target?.addEventListener(type, fn, { signal: abort.signal });
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      let visible = false, iframe = null, manual = false, failed = false;
      const privacy = () => {
        try {
          const p = window.Shopify?.customerPrivacy;
          return p?.analyticsProcessingAllowed() === true && p?.marketingAllowed() === true && p?.saleOfDataAllowed() === true;
        } catch (_) { return false; }
      };
      const motion = () => !reduce.matches && !navigator.connection?.saveData
        && !document.documentElement.dataset.miniamiMotionForcedStatic?.includes('true')
        && document.documentElement.dataset.miniamiMotionPaused !== 'true'
        && (document.body.dataset.miniamiMotionLevel || document.body.dataset.miniamiMotion || '').toLowerCase() !== 'off';
      const unload = () => {
        iframe?.remove(); iframe = null;
        actions.hidden = false;
        delete this.dataset.embedState;
        button.textContent = 'Play video'; manual = false;
      };
      const mount = () => {
        if (iframe || !this.isConnected || !visible || document.hidden || !privacy()) return;
        iframe = document.createElement('iframe');
        iframe.title = 'Making video on YouTube';
        iframe.loading = 'lazy';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&playsinline=1&controls=1&rel=0`;
        const mounted = iframe;
        on(mounted, 'load', () => {
          if (iframe === mounted) this.dataset.embedState = 'loaded'; // Document load, not proof of playback.
        });
        on(mounted, 'error', () => {
          if (iframe !== mounted) return;
          unload(); failed = true;
          status.textContent = 'The embedded video could not load. Try again or open on YouTube.';
        });
        actions.hidden = true;
        this.dataset.embedState = 'mounted';
        screen.append(iframe);
      };
      const sync = () => {
        if (!privacy()) {
          unload(); status.textContent = 'Embedded playback needs privacy permission. You can open the video on YouTube.'; return;
        }
        if (document.hidden || !visible || (!manual && !motion())) { unload(); return; }
        if (!iframe && !failed && motion()) mount();
        if (!iframe) status.textContent = 'Press Play to load the muted YouTube player.';
      };
      button.hidden = false;
      on(button, 'click', () => {
        if (!privacy()) { sync(); return; }
        unload(); failed = false; manual = true; mount();
      });
      on(document, 'visitorConsentCollected', sync);
      on(document, 'visibilitychange', sync);
      on(window, 'pageshow', sync);
      on(reduce, 'change', sync);
      on(navigator.connection, 'change', sync);
      const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.5; sync();
      }, { threshold: [0, 0.5] }) : null;
      observer?.observe(screen);
      const mutations = new MutationObserver(sync);
      mutations.observe(document.documentElement, { attributes: true, attributeFilter: ['data-miniami-motion-forced-static', 'data-miniami-motion-paused'] });
      mutations.observe(document.body, { attributes: true, attributeFilter: ['data-miniami-motion-level', 'data-miniami-motion'] });
      this.dispose = () => { abort.abort(); observer?.disconnect(); mutations.disconnect(); unload(); this.dispose = null; };
      loadPrivacy().then(() => { if (!abort.signal.aborted) sync(); });
    }
    disconnectedCallback() { this.dispose?.(); }
  });
})();
